import cx from 'classnames';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { ReactMarkdownOptions } from 'react-markdown/lib/react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { findAndActivatePhoneNumbersInMarkdown } from '@gov-nx/utils/common';
import type { UrlChecker } from '@gov-nx/utils/web';
import { MarkdownLink } from './MarkdownLink';

type MarkdownRenderExtraProps = {
	/**
	 * Find phone numbers in content and convert to tel: links.
	 * The phone numbers must start with + sign to be detected.
	 */
	activatePhoneNumbers?: boolean;
	/** Apply custom classes to markdown elements. */
	applyClasses?: {
		paragraph?: string;
		link?: string;
	};
	/** Additional checker function to test if a link should open in new window. */
	checkIfUrlShouldOpenNewWindowWith?: UrlChecker;
	/** Disable GitHub Flavored Markdown plugin. */
	disableGithubFlavoredMarkdown?: boolean;
	/** Disable remark breaks plugin. */
	disableRemarkBreaks?: boolean;
	/** Disable wrapping with <div.markdown-wrapper> element. */
	disableWrapper?: boolean;
	/**
	 * By default external links open in a new tab (target="_blank").
	 * Set this to true to keep them in the same tab (the external-link icon is also omitted).
	 */
	openExternalLinksInSameTab?: boolean;
	/**
	 * Render every link as a plain <a> instead of a router <Link>, so clicks
	 * always trigger a full browser navigation. Needed for absolute same-origin
	 * URLs that must hit the server (e.g. redirect/session flows).
	 */
	forceFullPageNavigation?: boolean;
	/**
	 * URLs in plain text are automatically converted to <a> elements.
	 * When processing them, we render the link without the protocol visible to a user.
	 *  default: https://example.com => <a href="https://example.com">example.com</a>
	 *
	 * This option allows you to switch this behavior off.
	 *  when true: https://example.com => <a href="https://example.com">https://example.com</a>
	 */
	includeProtocolInLinkLabels?: boolean;
	/**
	 * Override used plugins or disable all with an empty array.
	 *
	 * Note that disableRemarkBreaks and disableGithubFlavoredMarkdown
	 * have no effect if this property is set.
	 */
	remarkPlugins?: ReactMarkdownOptions['remarkPlugins'];
	/**
	 * CSS class/es to apply to wrapping <div> element. "markdown-wrapper" class is always applied.
	 * Ignored if disableWrapper is set to true.
	 */
	wrapperClassName?: string;
	testId?: string;
	id?: string;
	onInternalLinkClick?: (event: React.MouseEvent) => void;
};

type PoMarkdownRenderWebProps = ReactMarkdownOptions & MarkdownRenderExtraProps;

/**
 * Components using remark/react-markdown transparently.
 *
 * @remarks KNOWN INCOMPABILITY:
 * Usage inside of GovAccordionItem with custom paragraph classes.
 *   => throws NotFoundError: Node.removeChild: The node to be removed is not a child of this node.
 */
export const MarkdownRender: React.FC<PoMarkdownRenderWebProps> = (
	renderProps
) => {
	const {
		activatePhoneNumbers,
		applyClasses,
		checkIfUrlShouldOpenNewWindowWith: extraUrlChecker,
		components,
		disableGithubFlavoredMarkdown,
		disableRemarkBreaks,
		disableWrapper,
		includeProtocolInLinkLabels,
		openExternalLinksInSameTab,
		forceFullPageNavigation,
		remarkPlugins,
		wrapperClassName,
		onInternalLinkClick,
	} = renderProps;

	const customComponentRenderers: ReactMarkdownOptions['components'] = {
		a: ({ href, children }) => (
			<MarkdownLink
				href={href}
				linkClassName={applyClasses?.link}
				includeProtocolInLinkLabels={includeProtocolInLinkLabels}
				openExternalLinksInSameTab={openExternalLinksInSameTab}
				forceFullPageNavigation={forceFullPageNavigation}
				checkIfUrlShouldOpenNewWindowWith={extraUrlChecker}
				onInternalLinkClick={onInternalLinkClick}>
				{children}
			</MarkdownLink>
		),
	};

	if (applyClasses?.paragraph) {
		customComponentRenderers.p = (props) => (
			<p className={applyClasses?.paragraph}>{props.children}</p>
		);
	}

	let contents = renderProps.children;
	if (activatePhoneNumbers && typeof contents === 'string') {
		contents = findAndActivatePhoneNumbersInMarkdown(contents);
	}

	const finalProps = {
		...renderProps,

		skipHtml: true,
		remarkPlugins: remarkPlugins ?? [
			...(disableRemarkBreaks ? [] : [remarkBreaks]),
			...(disableGithubFlavoredMarkdown ? [] : [remarkGfm]),
		],
		components: {
			...customComponentRenderers,
			...components,
		},

		children: contents,
	};

	if (disableWrapper) {
		return <ReactMarkdown {...finalProps} />;
	}

	return (
		<div
			className={cx('markdown-wrapper', wrapperClassName)}
			id={finalProps.id}
			data-testid={finalProps.testId}>
			<ReactMarkdown {...finalProps} />
		</div>
	);
};
